import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { useGetInternalKeywordsQuery } from '../../api/keywords'
import { useGetInternalFeatureTagsQuery } from '../../api/screenshot-links'
import { useGetInternalConceptsQuery, useGetInternalTagsQuery } from '../../api/tags'
import { InternalTagType } from '../../types/InternalTag'
import { InternalFormDialog } from '../InternalFormDialog/InternalFormDialog'
import { AddFeaturesDialogFeature, AddFeaturesDialogShow, filterDialogConcepts, filterDialogFeatures, toDialogConcepts } from './addFeaturesDialogHelpers'

export type AddFeaturesSelection = { features: AddFeaturesDialogFeature[]; conceptIds: string[] }

type AddFeaturesDialogProps = {
  open: boolean
  // Without features the dialog offers concepts only.
  features?: AddFeaturesDialogFeature[]
  addedFeatureLegacyIds?: number[]
  addedConceptIds?: string[]
  title?: string
  onClose: () => void
  onAdd: (selection: AddFeaturesSelection) => void
}

const toggle = <T,>(values: T[], value: T) => (values.includes(value) ? values.filter((candidate) => candidate !== value) : [...values, value])

export const AddFeaturesDialog: FC<AddFeaturesDialogProps> = ({ open, features, addedFeatureLegacyIds = [], addedConceptIds = [], title, onClose, onAdd }) => {
  const { t } = useTranslation()
  const conceptsOnly = !features
  const featureTagsQuery = useGetInternalFeatureTagsQuery(undefined, { skip: !open })
  const keywordsQuery = useGetInternalKeywordsQuery(undefined, { skip: !open || conceptsOnly })
  const conceptsQuery = useGetInternalConceptsQuery(undefined, { skip: !open })
  const tagsQuery = useGetInternalTagsQuery(undefined, { skip: !open })
  const [search, setSearch] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [show, setShow] = useState<AddFeaturesDialogShow>('all')
  const [pickedFeatureIds, setPickedFeatureIds] = useState<number[]>([])
  const [pickedConceptIds, setPickedConceptIds] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    setSearch('')
    setSelectedTagIds([])
    setShow(conceptsOnly ? 'concepts' : 'all')
    setPickedFeatureIds([])
    setPickedConceptIds([])
  }, [open, conceptsOnly])

  const featureTags = useMemo(
    () => [...(featureTagsQuery.data ?? [])].filter((tag) => tag.active !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [featureTagsQuery.data]
  )
  const concepts = useMemo(
    () =>
      toDialogConcepts(
        conceptsQuery.data ?? [],
        (tagsQuery.data ?? []).filter((tag) => tag.type === InternalTagType.Concept)
      ),
    [conceptsQuery.data, tagsQuery.data]
  )
  const shownFeatures = useMemo(
    () =>
      conceptsOnly || show === 'concepts'
        ? []
        : filterDialogFeatures(features ?? [], { search, selectedTagIds, featureTags: featureTagsQuery.data ?? [], keywords: keywordsQuery.data ?? [] }),
    [conceptsOnly, show, features, search, selectedTagIds, featureTagsQuery.data, keywordsQuery.data]
  )
  const shownConcepts = useMemo(
    () => (show === 'features' ? [] : filterDialogConcepts(concepts, { search, selectedTagIds })),
    [show, concepts, search, selectedTagIds]
  )

  const isLoading = featureTagsQuery.isLoading || conceptsQuery.isLoading || tagsQuery.isLoading || (!conceptsOnly && keywordsQuery.isLoading)
  const failed = featureTagsQuery.isError || conceptsQuery.isError || tagsQuery.isError || keywordsQuery.isError
  const pickedCount = pickedFeatureIds.length + pickedConceptIds.length

  const handleAdd = () =>
    onAdd({
      features: (features ?? []).filter(({ legacyId }) => pickedFeatureIds.includes(legacyId)),
      conceptIds: concepts.filter(({ id }) => pickedConceptIds.includes(id)).map(({ id }) => id),
    })

  return (
    <InternalFormDialog
      open={open}
      title={title ?? t(conceptsOnly ? 'internal-analysts:add_concepts_title' : 'internal-analysts:add_features_title')}
      onClose={onClose}
      onSave={handleAdd}
      saveDisabled={!pickedCount}
      saveLabel={t('internal-analysts:add_features_add')}
      maxWidth="md"
      dividers
      extraActions={
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
          {t('internal-analysts:add_features_picked', { count: pickedCount })}
        </Typography>
      }
    >
      {isLoading ? (
        <Skeleton variant="rectangular" height={320} />
      ) : (
        <Stack spacing={2}>
          {failed && <Typography color="error.main">{t('internal-analysts:add_features_load_failed')}</Typography>}
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t(conceptsOnly ? 'internal-analysts:add_features_search_concepts' : 'internal-analysts:add_features_search')}
            inputProps={{ 'aria-label': t('internal-analysts:add_features_search_label') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t('internal-analysts:add_features_tags_help')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {featureTags.map((tag) => {
                const selected = selectedTagIds.includes(tag.id)
                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    aria-pressed={selected}
                    onClick={() => setSelectedTagIds((ids) => toggle(ids, tag.id))}
                  />
                )
              })}
            </Stack>
          </Box>
          {!conceptsOnly && (
            <RadioGroup row value={show} onChange={(event) => setShow(event.target.value as AddFeaturesDialogShow)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_all')} />
              <FormControlLabel value="features" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_features')} />
              <FormControlLabel value="concepts" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_concepts')} />
            </RadioGroup>
          )}
          {show !== 'concepts' && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('internal-analysts:add_features_features', { count: shownFeatures.length })}
              </Typography>
              <List dense disablePadding aria-label={t('internal-analysts:add_features_features_list')}>
                {shownFeatures.map((feature) => {
                  const added = addedFeatureLegacyIds.includes(feature.legacyId)
                  return (
                    <ListItemButton
                      key={feature.legacyId}
                      disabled={added}
                      onClick={() => setPickedFeatureIds((ids) => toggle(ids, feature.legacyId))}
                      sx={{ py: 0 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" size="small" checked={added || pickedFeatureIds.includes(feature.legacyId)} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.name}
                        secondary={added ? t('internal-analysts:add_features_added', { choice: feature.choice }) : feature.choice}
                      />
                    </ListItemButton>
                  )
                })}
              </List>
            </Box>
          )}
          {show !== 'features' && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('internal-analysts:add_features_concepts', { count: shownConcepts.length })}
              </Typography>
              <List dense disablePadding aria-label={t('internal-analysts:add_features_concepts_list')}>
                {shownConcepts.map((concept) => {
                  const added = addedConceptIds.includes(concept.id)
                  return (
                    <ListItemButton key={concept.id} disabled={added} onClick={() => setPickedConceptIds((ids) => toggle(ids, concept.id))} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" size="small" checked={added || pickedConceptIds.includes(concept.id)} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText primary={concept.name} secondary={added ? t('internal-analysts:add_features_concept_added') : undefined} />
                    </ListItemButton>
                  )
                })}
              </List>
            </Box>
          )}
        </Stack>
      )}
    </InternalFormDialog>
  )
}
